import cx from 'classnames';
import { useMemo } from 'react';
import VehicleMakeIcon from '@/common/VehicleMakeIcon';
import { useGetVehicleDetails } from '~/context/vdp/VehicleContext';
import useImageWithFallback, {
  type ImageMap
} from '~/hooks/common/ui/useImageWithFallback';
import optimizeImage from '~/utilities/optimizeImage';
import { HeroImage, Placeholder } from './WhyBuyMakePlaceholder.styles';

type Variant = 'card' | 'modal';

type OwnProps = {
  variant?: Variant;
};

enum ImageType {
  HeliosHero = 'heliosHero',
  Hero = 'hero'
}

const VARIANTS: Record<
  Variant,
  { heroWidth: number; logo?: string; logoSize: number }
> = {
  card: { heroWidth: 680, logoSize: 53 },
  modal: {
    heroWidth: 1080,
    logo: 'h-auto w-[22%]',
    logoSize: 60
  }
};

export default function WhyBuyMakePlaceholder({ variant = 'card' }: OwnProps) {
  const {
    make,
    vexVdpImageData: {
      heroes: { hero = '', vdpHeliosHeroDesktopURL = '' } = {}
    } = {}
  } = useGetVehicleDetails();
  const { heroWidth, logo, logoSize } = VARIANTS[variant];
  const heroImageMap: ImageMap<ImageType> = useMemo(
    () => ({
      [ImageType.HeliosHero]: {
        fallbackImageType: ImageType.Hero,
        imageType: ImageType.HeliosHero,
        shouldLogFallback: true,
        url: optimizeImage(vdpHeliosHeroDesktopURL, { width: heroWidth })
      },
      [ImageType.Hero]: {
        imageType: ImageType.Hero,
        url: optimizeImage(hero, { width: heroWidth })
      }
    }),
    [hero, heroWidth, vdpHeliosHeroDesktopURL]
  );
  const loadedHero = useImageWithFallback<ImageType>(
    heroImageMap,
    ImageType.HeliosHero,
    'WhyBuyMakePlaceholder'
  );

  return (
    <Placeholder data-testid="why-buy-media-placeholder">
      {loadedHero?.url && (
        <HeroImage
          alt=""
          aria-hidden
          className={cx(
            loadedHero.imageType === ImageType.HeliosHero && 'object-[50%_30%]',
            loadedHero.imageType === ImageType.Hero &&
              (variant === 'card'
                ? 'origin-[0%_30%] scale-[1.15] object-left'
                : 'object-left')
          )}
          data-testid="why-buy-placeholder-hero"
          draggable={false}
          src={loadedHero.url}
        />
      )}
      <VehicleMakeIcon
        aria-hidden
        className={cx('relative grayscale opacity-40', logo)}
        data-testid="why-buy-make-logo"
        make={make}
        size={logoSize}
      />
    </Placeholder>
  );
}
